import React, { memo, useCallback, useMemo } from 'react';
import { Slider, TextField, Switch, FormControlLabel, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { ModelSelector, ModelOption } from './ModelSelector';

export interface ParameterOption {
  value: string | number;
  label: string;
}

export interface Parameter {
  id: string;
  label: string;
  type: 'slider' | 'number' | 'boolean' | 'select';
  min?: number;
  max?: number;
  step?: number;
  default?: number | boolean | string;
  defaultValue?: number | boolean | string;
  options?: ParameterOption[];
}

export interface ModelSelectorConfig {
  label: string;
  value: string;
  valueKey: string;
  onChangeKey: string;
  models: ModelOption[];
  onChange: (value: string) => void;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
}

export interface ParameterControlsProps {
  parameters: Parameter[];
  values: Record<string, number | boolean | string>;
  onChange: (parameterId: string, value: number | boolean | string) => void;
  disabled?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
  modelSelector?: ModelSelectorConfig;
}

/**
 * Individual parameter control component - memoized to prevent re-renders
 */
interface ParameterControlProps {
  param: Parameter;
  value: number | boolean | string | undefined;
  onChange: (parameterId: string, value: number | boolean | string) => void;
  disabled: boolean;
  variant: 'positive' | 'negative' | 'neutral' | 'purple';
}

const ParameterControl = memo<ParameterControlProps>(({ param, value, onChange, disabled, variant }) => {
  // Memoize the change handler to prevent new references
  const handleSliderChange = useCallback((_: Event, newValue: number | number[]) => {
    onChange(param.id, newValue as number);
  }, [onChange, param.id]);

  const handleSelectChange = useCallback((e: any) => {
    onChange(param.id, e.target.value);
  }, [onChange, param.id]);

  const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(param.id, Number(e.target.value));
  }, [onChange, param.id]);

  const handleBooleanChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(param.id, e.target.checked);
  }, [onChange, param.id]);

  const actualValue = value ?? param.default ?? param.defaultValue;

  switch (param.type) {
    case 'select':
      return (
        <Box key={param.id} className="parameter-control nodrag" sx={{ marginBottom: '0' }}>
          <FormControl fullWidth size="small" disabled={disabled} className="nodrag">
            <InputLabel 
              className={`parameter-select-label-${variant}`}
              sx={{ 
                fontSize: '12px', 
                color: '#F3F4F6', 
                transform: 'translate(8px, 6px) scale(1)', 
                '&.MuiInputLabel-shrink': { transform: 'translate(12px, -6px) scale(0.75)' },
                '&.Mui-disabled': { color: 'var(--color-disabled-text)' }
              }}
            >
              {param.label}
            </InputLabel>
            <Select
              value={actualValue as string}
              onChange={handleSelectChange}
              label={param.label}
              className={`parameter-select parameter-select-${variant}`}
              sx={{ 
                fontSize: '12px',
                color: '#F3F4F6',
                '& .MuiSelect-select': { padding: '4px 8px', minHeight: 'auto', color: '#F3F4F6' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#27272A' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3F3F46' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3F3F46' },
                '&.Mui-disabled': {
                  color: 'var(--color-disabled-text)',
                  backgroundColor: 'var(--color-disabled-bg)',
                  '& .MuiSelect-select': { color: 'var(--color-disabled-text)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-disabled-border)' },
                  '& .MuiSelect-icon': { color: 'var(--color-disabled-text-muted)' }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 180,
                    backgroundColor: '#18181B',
                    border: '1px solid #52525B',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      minHeight: '28px',
                      padding: '4px 12px',
                      lineHeight: 1.2,
                      color: '#F3F4F6',
                      backgroundColor: '#18181B',
                      '&:hover': {
                        backgroundColor: '#27272A'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#27272A',
                        '&:hover': {
                          backgroundColor: '#3F3F46'
                        }
                      }
                    }
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                }
              }}
            >
              {(param.options || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );

    case 'slider':
      return (
        <Box key={param.id} className="parameter-control">
          <label className={`parameter-label ${disabled ? 'parameter-label-disabled' : ''}`}>{param.label}</label>
          <Box className="parameter-slider-container">
            <Slider
              value={actualValue as number}
              onChange={handleSliderChange}
              min={param.min ?? 0}
              max={param.max ?? 1}
              step={param.step ?? 0.1}
              disabled={disabled}
              valueLabelDisplay="auto"
              className={`parameter-slider parameter-slider-${variant}`}
              sx={{
                '&.Mui-disabled': {
                  color: 'var(--color-disabled-text)',
                  '& .MuiSlider-thumb': { backgroundColor: 'var(--color-disabled-text)' },
                  '& .MuiSlider-track': { backgroundColor: 'var(--color-disabled-text)' },
                  '& .MuiSlider-rail': { backgroundColor: 'var(--color-disabled-border)' }
                }
              }}
            />
            <span className={`parameter-value ${disabled ? 'parameter-value-disabled' : ''}`}>{actualValue}</span>
          </Box>
        </Box>
      );

    case 'number':
      return (
        <Box key={param.id} className="parameter-control">
          <TextField
            label={param.label}
            type="number"
            value={actualValue}
            onChange={handleNumberChange}
            inputProps={{
              min: param.min,
              max: param.max,
              step: param.step,
            }}
            disabled={disabled}
            size="small"
            fullWidth
            className={`parameter-number parameter-number-${variant}`}
            sx={{
              '& .MuiInputLabel-root.Mui-disabled': { color: 'var(--color-disabled-text)' },
              '& .MuiOutlinedInput-root.Mui-disabled': {
                backgroundColor: 'var(--color-disabled-bg)',
                '& input': { color: 'var(--color-disabled-text)', WebkitTextFillColor: 'var(--color-disabled-text)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-disabled-border)' }
              }
            }}
          />
        </Box>
      );

    case 'boolean':
      return (
        <Box key={param.id} className="parameter-control">
          <FormControlLabel
            control={
              <Switch
                checked={actualValue as boolean}
                onChange={handleBooleanChange}
                disabled={disabled}
                className={`parameter-switch parameter-switch-${variant}`}
                sx={{
                  '&.Mui-disabled': {
                    '& .MuiSwitch-thumb': { backgroundColor: 'var(--color-disabled-text)' },
                    '& .MuiSwitch-track': { backgroundColor: 'var(--color-disabled-border) !important' }
                  }
                }}
              />
            }
            label={param.label}
            sx={{
              '& .MuiFormControlLabel-label.Mui-disabled': { color: 'var(--color-disabled-text)' }
            }}
          />
        </Box>
      );

    default:
      return null;
  }
});

ParameterControl.displayName = 'ParameterControl';

const ParameterControlsComponent: React.FC<ParameterControlsProps> = ({
  parameters,
  values,
  onChange,
  disabled = false,
  variant = 'neutral',
  modelSelector,
}) => {
  // Memoize parameter rendering to avoid recreating on each render
  const renderedControls = useMemo(() => {
    return parameters.map((param) => (
      <ParameterControl
        key={param.id}
        param={param}
        value={values[param.id]}
        onChange={onChange}
        disabled={disabled}
        variant={variant}
      />
    ));
  }, [parameters, values, onChange, disabled, variant]);

  return (
    <Box className={`parameter-controls-container parameter-controls-${variant}`}>
      {modelSelector && (
        <ModelSelector
          label={modelSelector.label}
          value={modelSelector.value}
          onChange={modelSelector.onChange}
          models={modelSelector.models}
          disabled={disabled}
          variant={modelSelector.variant || variant}
        />
      )}
      {renderedControls}
    </Box>
  );
};

/**
 * Memoized ParameterControls component to prevent unnecessary re-renders
 * during node dragging and other canvas operations
 */
export const ParameterControls = memo(ParameterControlsComponent, (prevProps, nextProps) => {
  // Custom shallow comparison for better performance
  if (prevProps.disabled !== nextProps.disabled) return false;
  if (prevProps.variant !== nextProps.variant) return false;
  if (prevProps.onChange !== nextProps.onChange) return false;
  if (prevProps.parameters !== nextProps.parameters) return false;
  
  // Deep compare values object since it changes frequently
  const prevValues = prevProps.values;
  const nextValues = nextProps.values;
  if (prevValues !== nextValues) {
    const prevKeys = Object.keys(prevValues);
    const nextKeys = Object.keys(nextValues);
    if (prevKeys.length !== nextKeys.length) return false;
    for (const key of prevKeys) {
      if (prevValues[key] !== nextValues[key]) return false;
    }
  }
  
  // Compare modelSelector
  const prevModel = prevProps.modelSelector;
  const nextModel = nextProps.modelSelector;
  if (prevModel !== nextModel) {
    if (!prevModel || !nextModel) return false;
    if (prevModel.value !== nextModel.value) return false;
    if (prevModel.onChange !== nextModel.onChange) return false;
    if (prevModel.models !== nextModel.models) return false;
  }
  
  return true;
});
